import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed } from 'vue';

import { OrdersFilters } from './components/OrdersFilters';
import { OrdersTable } from './components/OrdersTable';
import { Pagination } from '@/modules/common/components/Pagination';
import { OrderModal } from '@/modules/trade/components/OrderModal';
import { Portal } from '@/modules/common/components/Portal';

import {
  getPortfolioOrders,
  PortfolioOrdersFilter,
  PortfolioOrdersFilters,
  PortfolioOrder,
  PortfolioOrdersPagination
} from '@/modules/wealth/services/portfolio';
import { OrdersFiltersKeys, IOrdersFilters, FetchOrdersStatus } from './types';
import { OrderDetailsResponse } from '@/modules/trade/services/orders';

interface Props {
  portfolioId: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'Orders',
  props: {
    portfolioId: null
  },
  setup(__props: any) {

const props = __props as {
  portfolioId: string;
}



let abortSignal: AbortController | null = null;
const status = ref<FetchOrdersStatus>(FetchOrdersStatus.Loading);
const pageIndex = ref(1);
const totalPages = ref(1);
const pageSize = ref(10);
const updatedAt = ref<null | string>(null);
const orders = ref<PortfolioOrder[]>([]);
const currentOrder = ref<OrderDetailsResponse | null>(null);
const isOrderModalOpen = ref(false);
const filters = ref<IOrdersFilters>({
  [OrdersFiltersKeys.Type]: {
    value: '',
    options: []
  }
});

const hasOrders = computed(() => {
  return orders.value.length > 0;
});

const isLoading = computed(() => {
  return status.value === FetchOrdersStatus.Loading;
});

const hasFilterSelected = computed(() => {
  const filtersKeys = Object.values(OrdersFiltersKeys);

  return filtersKeys.some(filterKey => !!filters.value[filterKey].value);
});

const hasFilters = computed(() => {
  const filtersKeys = Object.values(OrdersFiltersKeys);

  return filtersKeys.some(filterKey => {
    return !!filters.value[filterKey].options?.length;
  });
});

const isFiltersLoading = computed(() => {
  return !hasFilters.value && isLoading.value;
});

const openOrderModal = (order: PortfolioOrder) => {
  currentOrder.value = order.details;
  isOrderModalOpen.value = true;
};

const closeOrderModal = () => {
  isOrderModalOpen.value = false;
};

const setFilterValue = (filterKey: OrdersFiltersKeys, value: string) => {
  // When select 'all' option, the value is an empty string to prevent showing any option selected in the filter
  filters.value[filterKey].value =
    value.toLocaleLowerCase() === 'all' ? '' : value;
};

const setFilterOptions = (
  filterKey: OrdersFiltersKeys,
  options: PortfolioOrdersFilter[]
) => {
  filters.value[filterKey].options = options.map(option => ({
    value: option.value,
    label: option.formatted
  }));
};

const handleOnFilterSelection = async (
  filterKey: OrdersFiltersKeys,
  value: string
) => {
  setFilterValue(filterKey, value);
  await fetchOrders();
};

const handleClearFilters = async () => {
  const filtersKeys = Object.values(OrdersFiltersKeys);

  filtersKeys.forEach(filterKey => {
    setFilterValue(filterKey, '');
  });

  await fetchOrders();
};

const setFilters = (filters: PortfolioOrdersFilters) => {
  const selectedTypeFilter = filters.type.find(type => type.selected);
  setFilterOptions(OrdersFiltersKeys.Type, filters.type);
  setFilterValue(OrdersFiltersKeys.Type, selectedTypeFilter?.value || '');
};

const handleOnSelectPage = async (selectedPage: number) => {
  pageIndex.value = selectedPage;
  await fetchOrders();
};

const setPagination = (pagination: PortfolioOrdersPagination) => {
  pageIndex.value = pagination.pageIndex;
  totalPages.value = pagination.totalPages;
};

const fetchOrders = async () => {
  status.value = FetchOrdersStatus.Loading;
  try {
    if (abortSignal) abortSignal.abort();
    abortSignal = new AbortController();

    const params = {
      portfolioId: props.portfolioId,
      pageIndex: pageIndex.value,
      pageSize: pageSize.value,
      type: filters.value[OrdersFiltersKeys.Type].value || undefined
    };

    const data = await getPortfolioOrders(params, abortSignal.signal);

    orders.value = data.orders;
    updatedAt.value = data.updatedAt.formatted;
    setFilters(data.filters);
    setPagination(data.pagination);

    status.value = FetchOrdersStatus.Default;
  } catch {
    status.value = FetchOrdersStatus.Error;
  }
};

onMounted(async () => {
  await fetchOrders();
});

return { __sfc: true,props, abortSignal, status, pageIndex, totalPages, pageSize, updatedAt, orders, currentOrder, isOrderModalOpen, filters, hasOrders, isLoading, hasFilterSelected, hasFilters, isFiltersLoading, openOrderModal, closeOrderModal, setFilterValue, setFilterOptions, handleOnFilterSelection, handleClearFilters, setFilters, handleOnSelectPage, setPagination, fetchOrders, OrdersFilters, OrdersTable, Pagination, OrderModal, Portal }
}

})